import React from "react";
import { ExchangeRateDto, IUserDto } from "interfaces";
import { Raffle } from "types/raffles";
import { Stream } from "helpers/types";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import apiService, { ApiService } from "services/apiService";
import { ExceptionHandlingType } from "types/apiServiceTypes";

export type TAppNotifyType = "success" | "error" | "warn" | "info" | "debug";
export type TAppNotifyUnit = "milliseconds" | "seconds" | "minutes" | "hours" | "days";
export type TAppAccessFetch = (
  input: string | URL,
  init?: RequestInit | undefined,
  exceptionHandlingType?: ExceptionHandlingType
) => Promise<Response | void>;
export type TAppAccessURL = (input: string, origin?: string) => URL | void;
export type TAppNotify = (
  text: React.ReactElement[],
  timeout?: number,
  unit?: TAppNotifyUnit
) => void;
export type TAppNotifyAxios = (error: any) => void;
export type TAppNotifyRemove = (...ids: number[]) => void;

export interface IAppNotifyItem {
  id: number;
  type: TAppNotifyType;
  text: React.ReactElement;
  expiresAt?: number;
  timeout?: number;
  unit?: TAppNotifyUnit;
}

export interface BeAGuestDasboard {
  totalParticipants: number;
  probability: number;
}

export interface IAppContext {
  accessFetch: TAppAccessFetch;
  accessURL: TAppAccessURL;
  apiService: ApiService;
  newRaffle: Raffle | null;
  profile: IUserDto | null;
  isProfileLoading: boolean;
  refetchProfile: (
    options?: RefetchOptions | undefined
  ) => Promise<QueryObserverResult<IUserDto | null, Error>>;
  featureFlags: { code: string; enabled: boolean }[];
  isFeatureFlagsReady: boolean;
  isFeatureEnabled: (code: string) => boolean;
  // setProfile: React.Dispatch<React.SetStateAction<IUserDto | null>>;
  liveStreams: Stream[];
  showQrModal: boolean;
  setShowQrModal: React.Dispatch<React.SetStateAction<boolean>>;
  rates: ExchangeRateDto | null;
  setRates: React.Dispatch<React.SetStateAction<ExchangeRateDto | null>>;
  profileProps: {
    isProfileOpen: boolean;
    onOpenProfile: () => void;
    onCloseProfile: () => void;
  };
  loginModalProps: {
    isLoginOpen: boolean;
    onOpenLogin: () => void;
    onCloseLogin: () => void;
  };
  vipTransferModalProps: {
    isVipTransferOpen: boolean;
    onOpenVipTransfer: () => void;
    onCloseVipTransfer: () => void;
  };
  onDiscordRedirect: () => void;
  onSteamRedirect: () => void;
  onGoogleRedirect: () => void;
  onTwitchRedirect: () => void;
  onKickRedirect: () => void;
  onDiscordRefresh: () => void;
  onTwitchRefresh: () => void;
  onKickRefresh: () => void;
  // setUserBalance: React.Dispatch<
  //   React.SetStateAction<Pick<IUserDto, "gPointsBalance" | "pointsBalance">>
  // >;
  setUserBalance: (balance: Pick<IUserDto, "gPointsBalance" | "pointsBalance">) => void;
}

export const AppContext = React.createContext<IAppContext>({
  accessFetch: async () => console.info("AppContext.accessFetch"),
  accessURL: () => console.info("AppContext.accessURL"),
  apiService,
  showQrModal: false,
  setShowQrModal: () => console.info("AppContext.setShowLogin"),
  profile: null,
  featureFlags: [],
  isFeatureFlagsReady: false,
  isFeatureEnabled: () => false,
  newRaffle: null,
  liveStreams: [],
  rates: null,
  setRates: () => console.info("AppContext.setRates"),
  profileProps: {
    isProfileOpen: false,
    onOpenProfile: () => console.info("AppContext.onOpenProfile"),
    onCloseProfile: () => console.info("AppContext.onCloseProfile"),
  },
  loginModalProps: {
    isLoginOpen: false,
    onOpenLogin: () => console.info("AppContext.onOpenLogin"),
    onCloseLogin: () => console.info("AppContext.onCloseLogin"),
  },
  vipTransferModalProps: {
    isVipTransferOpen: false,
    onOpenVipTransfer: () => console.info("AppContext.onOpenVipTransfer"),
    onCloseVipTransfer: () => console.info("AppContext.onCloseVipTransfer"),
  },
  isProfileLoading: false,
  refetchProfile: async () => Promise.reject(),
  setUserBalance: () => console.info("AppContext.setUserBalance"),
  onKickRedirect: () => console.info("AppContext.onKickRedirect"),
  onDiscordRedirect: () => console.info("AppContext.onDiscordRedirect"),
  onSteamRedirect: () => console.info("AppContext.onSteamRedirect"),
  onGoogleRedirect: () => console.info("AppContext.onGoogleRedirect"),
  onTwitchRedirect: () => console.info("AppContext.onTwitchRedirect"),
  onDiscordRefresh: () => console.info("AppContext.onDiscordRefresh"),
  onTwitchRefresh: () => console.info("AppContext.onTwitchRefresh"),
  onKickRefresh: () => console.info("AppContext.onKickRefresh"),
});

export function useAppContext() {
  const context = React.useContext(AppContext);
  if (context === undefined) {
    throw new Error("useAppContext must be used within a AppProvider");
  }
  return context;
}
